import type { CourtRuleAuthoritySource, PendingApproval, PendingInteraction, RuntimeAnswer } from '../types/runtime';
import { ApprovalRequestCard } from './answers/ApprovalRequestCard';
import { BillingCapabilitiesCard } from './answers/BillingCapabilitiesCard';
import { BillingSnapshotCard } from './answers/BillingSnapshotCard';
import { CalendarDeadlineAnswerCard } from './answers/CalendarDeadlineAnswerCard';
import { ClarificationCard } from './answers/ClarificationCard';
import { CourtRulesAnswerCard } from './answers/CourtRulesAnswerCard';
import { DebugStateCard } from './answers/DebugStateCard';
import { DocumentEvidenceCard } from './answers/DocumentEvidenceCard';
import { DocumentScopeInteractionCard } from './answers/DocumentScopeInteractionCard';
import { EmailLinkedListCard } from './answers/EmailLinkedListCard';
import { EmailThreadDetailCard } from './answers/EmailThreadDetailCard';
import { MatterPartiesCard } from './answers/MatterPartiesCard';
import { MatterPostureCard } from './answers/MatterPostureCard';
import { MatterSummaryCard } from './answers/MatterSummaryCard';
import { MatterTimelineCard } from './answers/MatterTimelineCard';
import { ModelSmokeTestCard } from './answers/ModelSmokeTestCard';
import { ScopedAdviceCard } from './answers/ScopedAdviceCard';
import { TaskCompletedCard } from './answers/TaskCompletedCard';
import { TaskCompletionFailedCard } from './answers/TaskCompletionFailedCard';
import { TaskCompletionRejectedCard } from './answers/TaskCompletionRejectedCard';
import { TaskListCard } from './answers/TaskListCard';
import { TaskOverdueCard } from './answers/TaskOverdueCard';
import { UnsupportedCard } from './answers/UnsupportedCard';
import { WorkspaceOpenerCard } from './answers/WorkspaceOpenerCard';

interface Props {
  answer: RuntimeAnswer;
  /**
   * Phase 8: when the runtime paused at an interrupt, the API surfaces the
   * payload via `pending_approval`. If present, render the approval card
   * instead of (or alongside) the deterministic answer card.
   */
  pendingApproval?: PendingApproval | null;
  pendingInteraction?: PendingInteraction | null;
  onEvidenceSelect?: (evidenceId: string) => void;
  onCourtRuleSourceOpen?: (source: CourtRuleAuthoritySource) => void;
}

export function LexAnswerCard({
  answer,
  pendingApproval,
  pendingInteraction,
  onEvidenceSelect,
  onCourtRuleSourceOpen,
}: Props) {
  // The pending_approval card takes priority — when the graph is waiting on
  // a decision, we show the approve/reject buttons. The placeholder answer
  // payload (kind=pending_approval) just carries the summary text, but the
  // ApprovalRequestCard renders a richer view.
  if (pendingApproval) {
    return <ApprovalRequestCard approval={pendingApproval} />;
  }
  if (pendingInteraction) {
    return <DocumentScopeInteractionCard interaction={pendingInteraction} />;
  }

  switch (answer.kind) {
    case 'workspace_opener':
    case 'workspace_matter_list':
      return <WorkspaceOpenerCard answer={answer} />;
    case 'matter_linked_communications':
    case 'email_metadata_lookup':
    case 'email_corpus_count':
    case 'email_topic_grouped_summary':
      return <EmailLinkedListCard answer={answer} />;
    case 'email_thread_detail':
      return <EmailThreadDetailCard answer={answer} />;
    case 'matter_summary':
      return <MatterSummaryCard answer={answer} />;
    case 'matter_posture':
      return <MatterPostureCard answer={answer} />;
    case 'matter_timeline':
      return <MatterTimelineCard answer={answer} />;
    case 'matter_parties':
      return <MatterPartiesCard answer={answer} />;
    case 'matter_unsupported':
    case 'matter_not_found':
      return <UnsupportedCard answer={answer} />;
    case 'task_list':
      return <TaskListCard answer={answer} />;
    case 'task_overdue':
      return <TaskOverdueCard answer={answer} />;
    case 'task_unsupported':
      return <UnsupportedCard answer={answer} />;
    case 'task_completed':
      return <TaskCompletedCard answer={answer} />;
    case 'task_completion_rejected':
      return <TaskCompletionRejectedCard answer={answer} />;
    case 'task_completion_failed':
      return <TaskCompletionFailedCard answer={answer} />;
    case 'billing_snapshot':
    case 'billing_multi_matter_snapshot':
      return <BillingSnapshotCard answer={answer} />;
    case 'billing_capabilities':
      return <BillingCapabilitiesCard answer={answer} />;
    case 'billing_unsupported':
    case 'billing_not_found':
      return <UnsupportedCard answer={answer} />;
    case 'scoped_advice':
      return <ScopedAdviceCard answer={answer} />;
    case 'model_smoke_test':
    case 'model_smoke_test_failed':
      return <ModelSmokeTestCard answer={answer} />;
    case 'clarification':
    case 'contextual_clarification':
      return <ClarificationCard answer={answer} />;
    case 'email_unsupported':
    case 'document_unsupported':
      return <UnsupportedCard answer={answer} />;
    case 'document_evidence_summary':
    case 'document_ephemeral_summary':
    case 'deep_corpus_synthesis':
      return <DocumentEvidenceCard answer={answer} onEvidenceSelect={onEvidenceSelect} />;
    case 'deep_corpus_limitation':
    case 'document_no_verified_evidence':
      return <UnsupportedCard answer={answer} />;
    case 'court_rules_issue_resolution':
    case 'court_rules_comparison_authority':
    case 'court_rules_no_candidate':
    case 'court_rules_unavailable':
      return <CourtRulesAnswerCard answer={answer} onOpenSource={onCourtRuleSourceOpen} />;
    case 'calendar_deadline_answer':
      return <CalendarDeadlineAnswerCard answer={answer} />;
    case 'routed_state_only':
      return <DebugStateCard answer={answer} />;
    default:
      return <DebugStateCard answer={answer} />;
  }
}
